/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { truncate, replace, isNil, map } from 'lodash';
import { Tag, Header, MaterialIcon, BrandColors, Text, LoadingSpinner } from '@blend/enterprise-components';
import { Message } from '../../components/Message';
import { NewMessageContainer } from '../../containers/NewMessage';
import type { ReduxProps } from './ThreadDetailsContainer';
import { useContext } from 'react';
import { InboxContext } from '../Inbox/InboxContext';

const styles = StyleSheet.create({
  backBtnWrapper: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    width: 160,
  },
  backTextWrapper: {
    marginLeft: 6,
  },
  condensedBackBtnWrapper: {
    padding: '14px 0 20px 22px',
  },
  condensedHeaderWrapper: {
    paddingBottom: 15,
    paddingLeft: 24,
  },
  header: {
    marginRight: 12,
  },
  headerWrapper: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: 20,
    marginTop: 10,
  },
  loadingSpinnerWrapper: {
    backgroundColor: BrandColors.WHITE,
    border: `1px solid ${BrandColors.CLOUD_50}`,

    display: 'flex',
    justifyContent: 'center',
    marginBottom: 10,
    padding: '40px 0',
  },
  messageWrapper: {
    margin: '10px 0',
  },
  threadDetailsWrapper: {
    paddingBottom: 60,
  },
});

export type ThreadDetailsProps = ReduxProps;
export const ThreadDetails: React.FC<ThreadDetailsProps> = ({ selectedThread, removeSelectedThread }) => {
  const {
    stylingConfig: { condensedDetails },
  } = useContext(InboxContext);

  const renderSubject = () => {
    return (
      <div className={css(styles.headerWrapper)} data-it="ThreadDetails-Header">
        <div className={css(styles.header)}>
          <Header size={3}>{truncate(selectedThread!.subject, { length: 108 })}</Header>
        </div>
        {<Tag text={replace(selectedThread!.topic, '_', ' ')} theme="default" />}
      </div>
    );
  };

  const renderCondensedSubject = () => {
    return (
      <div className={css(styles.condensedHeaderWrapper)} data-it="ThreadDetails-Header">
        <Header size={4}>{truncate(selectedThread!.subject, { length: 140 })}</Header>
      </div>
    );
  };

  const renderLoadingSpinner = () => (
    <div className={css(styles.loadingSpinnerWrapper)} id="it-ThreadDetails-spinner">
      <LoadingSpinner size="lg" theme="secondary" />
    </div>
  );

  const renderMessages = () => {
    const messages = selectedThread!.messages;
    return isNil(messages)
      ? renderLoadingSpinner()
      : map(messages, message => (
          <div className={css(!condensedDetails && styles.messageWrapper)}>
            <Message key={message.id} message={message} />
          </div>
        ));
  };

  return (
    <div className={css(styles.threadDetailsWrapper)} data-it="threadDetailsWrapper">
      <div
        className={css(styles.backBtnWrapper, condensedDetails && styles.condensedBackBtnWrapper)}
        id="it-ThreadDetails-backBtn"
        onClick={() => removeSelectedThread()}
      >
        <MaterialIcon color={BrandColors.NIGHT_80} size={22} type="arrow_back" />
        <div className={css(styles.backTextWrapper)}>
          <Text color={BrandColors.NIGHT_70} fontSize="md">
            Back to Inbox
          </Text>
        </div>
      </div>

      {condensedDetails ? renderCondensedSubject() : renderSubject()}
      {renderMessages()}
      <NewMessageContainer />
    </div>
  );
};
